import { createStore } from 'vuex';
import {IFundamentalData, IState} from '@/store/types/IState';
import {ILatLng, IOrderRequest} from '@/store/types/Order';
import {AxiosResponse} from 'axios';
import {
    IIcsFileData,
    ISetAppointmentRequest
} from '@/store/types/Appointment';
import {IDocument, IDocumentData, IDocumentRequest, IDocumentsRequest} from '@/store/types/Document';
import {Appointment} from '@/components/modules/types/Calendar';
import moment from 'moment';
import {IBookableService, IBookableServicesApi, IBookedService, IBookServicesApi, IBookServicesRequest, IPrice} from '@/store/types/Service';
import '@/extensions/StringExtensions';
import i18n, {resolveTmsLocale, getLocale, setLocale} from '@/i18n/index';
import CalendarHelper from '@/helpers/CalendarHelper';
import BookableServiceFilter from '@/helpers/BookableServiceFilter';
import ViewportHelper from '@/helpers/ViewportHelper';
import VuexPersist from 'vuex-persist';
import {orderDetailService} from '@/services/OrderDetailService';
import {featureFlags} from "@/store/feature-flag.store";
import {legacyApiService} from "@/services/ApiService";
import { appointmentService } from '@/services/AppointmentService';
import {OrderDetailResponseOrder, OrderDetailResponseOrderService} from "@/services/types/OrderDetailResponse";
import { bookableServicesService } from '@/services/BookableServiceService';
import { contactFormService } from '@/services/ContactFormService';
import { trackingService } from '@/services/TrackingService';
import { DeliveryTruckMapData } from '@/services/types/TrackingMapData';
import EnvironmentService from "@/services/EnvironmentService";
import {pollUntil} from "@/utils/pollUntil";

const vuexLocalStorage = new VuexPersist({
  key: 'vuex-state',
  storage: window.sessionStorage,
})
const calendarHelper = new CalendarHelper();
const bookableServiceFilter = new BookableServiceFilter();
const viewportHelper = new ViewportHelper();

const apiEndPoints = {
  /*   Getters   */
  getIcsFile: 'api/appointment/getIcsFile',
  getOrder: 'api/order',
  getCanScheduleOrder: "api/order/schedulability",
  getDocuments: 'api/documents',
  getAppointments: 'api/appointment',
  getGeoCoordinates: 'api/geo/getGeoCodeViaAddress',
  getBookableServices: 'api/services',
  /*   Setters   */
  setAppointment: 'api/appointment/setAppointment',
  /*   Misc      */
  sendContactEmail: 'api/contact',
  bookServices: 'api/services/bookServices'
};
const stepper = {
  withAdditionalServices: 3,
  withoutAdditionalServices: 2,
};

export function setNotification(order: OrderDetailResponseOrder) {
  const isSelfSchedulable = order.allowSelfScheduling;
  const hasReachedSchedulableStatus = order.isSchedulingStatusReached;


  const isCancelled = order.currentStatus === 'Cancelled'
  const isCompleted = order.currentStatus === 'Fulfilled';
  const notDelivered = order.currentStatus === 'NotDelivered';
  const onWayToCustomer = order.currentStatus === 'OnWayToCustomer';
  const deliveryDelayed = false;            // TODO: Not Migrated
  const timeWindowNarrowed = false;      // TODO: Not Migrated
  const fixedDateChanged = false;          // TODO: Not Migrated
  const hasAppointment = order.deliveryDate;

  // Order created
  let descriptionKey = 'chooseAppointment';

  if (isCancelled) {
    descriptionKey = "orderCanceled";
  } else if (isCompleted) {
    descriptionKey = "orderComplete";
    // Unsuccessful delivery
  } else if (notDelivered) {
    descriptionKey = "deliveryUnsuccessful";
    // Delayed
  } else if (deliveryDelayed) {
    descriptionKey = "deliveryDelayed";
    // Time window narrowed
  } else if (timeWindowNarrowed) {
    descriptionKey = "timeWindowNarrowed";
    // Delivery Enroute
  } else if (onWayToCustomer) {
    descriptionKey = "deliveryEnRoute";
    // Date changed
  } else if (fixedDateChanged) {
    descriptionKey = "fixedDateChanged";
    // Date set
  } else if (hasAppointment) {
    descriptionKey = "fixedDate";
  } else if (!isSelfSchedulable) {
    descriptionKey = "orderNotSelfSchedulable";
  } else if (!hasReachedSchedulableStatus) {
    descriptionKey = "orderNotReachedSchedulableStatus";
    // Completed
  }

  return i18n.global.t('notificationBox.description.' + descriptionKey) as string;
}

export function translateOrderStatus(orderStatus: string): string {
  switch(orderStatus) {
    case 'Fulfilled':
      return i18n.global.t('order.orderState.delivered').toString();
    case 'LoadedOnDistributionTruck':
    case 'Picked':
    case 'ReceivedAtHub':
    case 'GoodsAvailable':
    case 'GoodsLoaded':
    case 'LoadedOnDeliveryTruck':
      return i18n.global.t('order.orderState.goodsReceived').toString();
    case 'OrderCreated':
      return i18n.global.t('order.orderState.created').toString();
    case 'Clarification':
    case 'NotDelivered':
      return i18n.global.t('order.orderState.deliveryStopped').toString();
    case 'OnWayToCustomer':
      return i18n.global.t('order.orderState.inDelivery').toString();
    case 'Cancelled':
      return i18n.global.t('order.orderState.cancelled').toString();
    case 'PartiallyReceivedAtHub':
    case 'GoodsNotAvailable':
      return i18n.global.t('order.orderState.problemInDepot').toString();
    default:
      return orderStatus;
  }
}

const store = createStore({
  state: {
    language: 'de',
    isLoggedIn: false,
    blockScroll: false,
    isStartup: true,
    hasNews: true,
    notificationDescription: '',
    order: {} as OrderDetailResponseOrder,
    customerAppointments: [],
    bookableServices: [],
    bookedServices: [],
    selectedAppointmentId: '',
    appointmentError: false,
    clientLatLng: {} as ILatLng,
    overlappingTimes: 0,
    minStickyBottom: 45,
    stickyBottom: 0,
    customErrorMessage: '',
    lastRequestDate: null,
  } as IState,

  getters: {
    getOrder(state: IState) {
      return state.order;
    },
    getDeliveryArticleCount(state: IState) {
      return state.order.deliveryArticleCount;
    },
    getAppointments(state: IState) {
      return state.customerAppointments;
    },
    getOverlappingTimes(state: IState) {
      return state.overlappingTimes;
    },
    getExternalOrderNumber(state: IState) {
      return state.order.orderNumber;
    },
    getAddress(state: IState) {
      return state.order.contactData;
    },
    getCanMakeAppointment(state: IState) {
      return state.order.allowSelfScheduling &&
        !state.order.deliveryDate || (!!state.order.isSelfSchedulingPossibleUntil &&
          new Date(state.order.isSelfSchedulingPossibleUntil) > new Date());
    },
    getHasAppointment(state: IState) {
      return !!state.order.deliveryDate;
    },
    getSelectedAppointment(state: IState) {
      if (!state.selectedAppointmentId) {
        return null;
      }

      const selectedAppointment =
        state.customerAppointments.filter((x: { id: string }) => x.id === state.selectedAppointmentId)[0];

      if (!selectedAppointment.timeslot) {
        selectedAppointment.timeslot = moment(selectedAppointment.start).format('HH:mm') +
          ' - ' +
          moment(selectedAppointment.end).format('HH:mm');

        // ENDCP-191 - Don't show 'Hodiny' in timeslot on confirmation page
        if (getLocale() !== "cz")
          selectedAppointment.timeslot += ' ' + i18n.global.t('general.clock');
      }

      return selectedAppointment;
    },
    getEarliestAppointment(state: IState) {
      if (!state.customerAppointments || !state.customerAppointments.length) {
        return null;
      }

      return state.customerAppointments[0];
    },
    getStepperValue(state: IState) {
      if (state.bookableServices.length > 0) {
        return stepper.withAdditionalServices;
      } else {
        return stepper.withoutAdditionalServices;
      }
    },
    getLogoName(state: IState) {
      try {
        let clientKey = state.order.clientKey
        // TODO: clientKey TCHI is different in TMS and Cloud, when getting rid of legacy apis,
        //  we need to rename the logo icon as well
        if (clientKey === 'TCHI') {
          clientKey = 'Tchi';
        }
        return require(`@/assets/icons/clientlogos/${clientKey}.svg`);
      } catch (error) {
        return "";
      }
    },
    isIkeaClient(state: IState) {
      return state.order.clientDisplayName === 'IKEA';
    },
    contactFormIsVisible(state: IState) {
      return EnvironmentService.applicationConfiguration.clientsExcludedFromContactForm.split(',').includes(state.order.clientKey);
    },
    showCompensateEmissions(state: IState) {
      if (!state.order.clientKey || state.language !== 'de') {
        return false;
      }
      return EnvironmentService.applicationConfiguration.showCompensateEmissionsForClients.split(',').includes(state.order.clientKey);
    },
    allowTracking(){
      return (getLocale() === "de" || getLocale() === "pl" || getLocale() === "cz");
    }
  },
  mutations: {
    LOCALIZATION(state: IState, language: string) {
      state.language = language;
    },
    LOGIN(state: IState) {
      state.isLoggedIn = true;
      state.isStartup = true;
      state.hasNews = true;
      state.lastRequestDate = new Date();
    },
    LOGOUT(state: IState) {
      state.isLoggedIn = false;
      state.appointmentError = false;
      state.lastRequestDate = null;
      state.order = {} as OrderDetailResponseOrder;
      featureFlags.setClientKey('');
    },
    NEW_REQUEST(state: IState) {
      state.lastRequestDate = new Date();
    },
    END_STARTUP(state: IState) {
      state.isStartup = false;
    },
    DISABLE_NEWS(state: IState) {
      state.hasNews = false;
    },
    SET_ORDER(state: IState, value: OrderDetailResponseOrder) {
      state.order = value;
      state.notificationDescription = setNotification(state.order);
    },
    SET_APPOINTMENT_AND_SERVICES(state: IState, value: any) {
      state.order.deliveryDate = value.deliveryDate;
      state.order.deliveryTimeWindowFrom = value.deliveryTimeWindowFrom;
      state.order.deliveryTimeWindowTo = value.deliveryTimeWindowTo;
      state.order.services = value.services;
      state.notificationDescription = setNotification(state.order);
    },
    SET_CLIENTLNGLAT(state: IState, clientLngLat: ILatLng) {
      state.clientLatLng = clientLngLat;
    },
    SET_BOOKEDSERVICES(state: IState, bookedServices: IBookableService[]) {
      state.bookedServices = bookedServices;
    },
    BLOCK_SCROLL(state: IState, blockscroll: boolean) {
      const body = document.querySelector('body');
      if (body)
        blockscroll ? body.style.overflowY = 'hidden' : body.style.removeProperty('overflow-y');

      state.blockScroll = blockscroll;
    },
    SET_APPOINTMENTS(state: IState, appointments: Appointment[]) {
      state.customerAppointments = appointments;
    },
    SET_ALTERNATELANE(state: IState) {
      let max = 7;
      const appointments = state.customerAppointments;
      if (appointments.length > 1) {
        const selectedWeek = calendarHelper.getAppointmentWeekStart(appointments);
        const appointmentsWithinWeek = calendarHelper.getMaxAlternateLane(appointments, appointments.length, 7, selectedWeek);
        const appointmentsInWeek = appointmentsWithinWeek.map(u => u.alternateLane);
        max = Math.max(...appointmentsInWeek) + 1;
      }
      state.overlappingTimes = max;
    },
    SET_BOOKABLESERVICES(state: IState, bookableServices: IBookableService[]) {
      state.bookableServices = bookableServiceFilter.getFilteredBookableServicesPackagingDisposal(bookableServices, state.order);
    },
    SET_SELECTED_APPOINTMENT(state: IState, value: string) {
      state.selectedAppointmentId = value;
    },
    SET_APPOINTMENT_ERROR(state: IState, value: boolean) {
      state.appointmentError = value;
    },
    SET_STICKYBOTTOM(state: IState, value?: number) {
      state.stickyBottom = value || state.minStickyBottom;
    },
    SET_CUSTOM_ERROR_MESSAGE(state: IState, value?: string) {
      state.customErrorMessage = value || '';
    },
    UPDATE_DELIVERY_TRUCK_MAP_DATA(state: IState, value?: DeliveryTruckMapData) {
      if (value) {
        if (!state.actualInformationTo) {
          state.actualInformationTo = {
            gpsLatitude: "",
            gpsLongitude: "",
            remainingOrders: "",
            driverName: "",
            estimatedArrivalTime: "",
            speed: ""
          };
        }

        state.actualInformationTo.gpsLatitude = value.gpsLatitude.toString();
        state.actualInformationTo.gpsLongitude = value.gpsLongitude.toString();
        state.actualInformationTo.remainingOrders = value.remainingOrders.toString();
      }
    },
    SET_NOTIFICATION_TEXT(state: IState, value: string){
      state.notificationDescription = value;
    }
  },

  actions: {
    requestCanMakeAppointment() {
    },
    async requestOrderDetails({commit}, payload: IOrderRequest) {
          const response = payload.encryptionLoginType ? await orderDetailService.GetByOrderNumberAndHash(payload) :
              await orderDetailService.GetByOrderNumberAndPostalCode(payload);

          try {
              if (!response.isOk) {
                  return false;
              }
               const country = response.order!.contactData.orderAddressCountry;
               commit('SET_ORDER', response.order);

              const shortLocalCode = resolveTmsLocale(country == null ? '' : country);
              if (shortLocalCode !== 'de') {
                  setLocale(shortLocalCode);
              }
              commit('LOCALIZATION', shortLocalCode);
              return true;
          } catch {
              return false;
          }
    },

    async requestLegacyOrder(_, payload: IOrderRequest): Promise<IFundamentalData>
    {
      const legacyResponse = await legacyApiService.post<IFundamentalData>('api/order', payload);
      return legacyResponse.data as IFundamentalData;
    },

    requestDocuments(_,payload: IDocumentsRequest): Promise<IDocument[]> {

    return legacyApiService.get(apiEndPoints.getDocuments, payload)
        .then((response: AxiosResponse<IDocumentData>) => {
          if (response.status !== 200) {
              return [];
            }
          return response.data.documents;
        });
    },

    requestDocument(_, payload: IDocumentRequest) {
      return legacyApiService.getBlob(payload.url);
    },

    async requestIcsFile() {
      const order = store.state.order as OrderDetailResponseOrder;
      const address = order.contactData;
      const appointment = store.getters.getSelectedAppointment as Appointment;

      const date = calendarHelper.getFormattedDateForDisplay(appointment.start);
      const emailBody = i18n.global.t('appointment.summary.icsFileEmailBody', { name: `${address.customerName}`, clientName: order.clientDisplayName, orderNumber: order.orderNumber });
      const emailSubject = i18n.global.t('appointment.summary.icsFileEmailSubject', { clientName: order.clientDisplayName, orderNumber: order.orderNumber, date })
      const icsFileData = {
        emailBody: emailBody,
        emailSubject: emailSubject,
        fromTime: calendarHelper.getFormattedDateTime(appointment.start),
        toTime: calendarHelper.getFormattedDateTime(appointment.end),
        address: `${address.orderAddressStreet} ${address.orderAddressHouseNumber}, ${address.orderAddressPostCode}`
      } as IIcsFileData;

    return await appointmentService.getAppointmentIcsFile(icsFileData);
    },

    async requestCustomerAppointments({ commit }) {
      const internalOrderId = store.state.order.internalOrderId;
      const countryCode = store.state.order.contactData.orderAddressCountry;
      try
      {
        const appointments = await appointmentService.getAppointments(internalOrderId, countryCode);
        commit('SET_APPOINTMENTS', appointments);
        // Mutations are synchronous. The commit on SET_ALTERNATELANE will use the updated appointments (from SET_APPOINTMENTS).
        commit('SET_ALTERNATELANE');
        commit('SET_APPOINTMENT_ERROR', false);
        return null;
      }
      catch
      {
        //handling error
        commit('SET_APPOINTMENT_ERROR', true);
      }
    },

    async requestBookableServices({ commit }) {
      const data = {
        externalOrderNumber: store.state.order.orderNumber,
        internalOrderId: store.state.order.internalOrderId
      } as IBookableServicesApi;

      try{
        const services = await bookableServicesService.GetBookableServices(data);
        commit('SET_BOOKABLESERVICES', services);
        return null;
      }
      catch
      {
        return null;
      }
    },

    async bookServices({ commit }) {
      if (!this.state.bookedServices.length) {
        return;
      }
      const curDateString = new Date(Date.now()).toUTCString();

      const data = {
        bookServicesRequest: this.state.order.internalOrderId
          ? ({
              internalOrderId: this.state.order.internalOrderId,
              serviceKeys: this.state.bookedServices.map(service => {
                return service.key;
                })
              } as IBookServicesRequest)
              : {} as IBookServicesRequest,
            clientKey: this.state.order.clientKey,
            externalOrderNumber: this.state.order.orderNumber,
            language: getLocale(),
            articlesOnOrder: store.getters.getDeliveryArticleCount,
            services: this.state.bookedServices.map((service) => {
          return {
            serviceName: service.key,
            servicePrice: {
              amount: service.sumPrice,
              currency: store.getters.getSelectedAppointment.currency
            } as IPrice,
            creationDateTime: calendarHelper.getFormattedDateTime(curDateString)
          } as IBookedService
        })
      } as IBookServicesApi;
      return bookableServicesService.BookServices(data).then(() => {
        commit('SET_BOOKABLESERVICES', this.state.bookableServices.filter(s =>
          !this.state.bookedServices.map(b => b.key).includes(s.key)));
      });
    },
    setSelectedAppointmentAndServices({ commit }) {
      const appointment = store.getters.getSelectedAppointment as Appointment;
      const deliveryQuantity = store.state.order.shipments.reduce((total, value) => total + value.orderedQuantity, 0);
      const services: OrderDetailResponseOrderService[] = store.state.bookedServices.map(s => ({
        name: s.description,
        deliveryQuantity
      }));
      commit('SET_APPOINTMENT_AND_SERVICES', {
        deliveryDate: moment.utc(appointment.date).format('YYYY-MM-DDTHH:mm:ssZ'),
        deliveryTimeWindowFrom: moment(appointment.start).format('YYYY-MM-DDTHH:mm:ssZ'),
        deliveryTimeWindowTo: moment(appointment.end).format('YYYY-MM-DDTHH:mm:ssZ'),
        services: services,
      });
    },
    async setCustomerAppointment() {

      const appointment = store.getters.getSelectedAppointment as Appointment;

      const confirmedAppointment = {
        appointment: appointment.date,
        fromTime: moment(appointment.start).format('HH:mm'),
        toTime: moment(appointment.end).format('HH:mm'),
        costs: appointment.price.toString(),
        currency: appointment.currency,
        internalOrderNumber: '',
        earliestAppointment: store.getters.getEarliestAppointment.date,
        priceId: appointment.priceId,
        class: appointment.class,
        externalOrderNumber: store.state.order.orderNumber,
        clientKey: store.state.order.clientKey,
        internalOrderId: store.state.order.internalOrderId,
        id: appointment.id,
      } as ISetAppointmentRequest;

      const updated = store.getters.getOrder.updated;
      return appointmentService.setAppointments(confirmedAppointment).then(() => {
        return pollUntil(async () => {
          await store.dispatch('reloadOrderDetails');
          return updated < store.getters.getOrder.updated;
        }).then(() => store.dispatch("requestBookableServices"));
      });
    },

    async reloadOrderDetails() {
      const order = store.state.order;

      const request = {
        externalOrderNumber: order.orderNumber,
        postcode: order.contactData.orderAddressPostCode,
      } as IOrderRequest;

      await store.dispatch('requestOrderDetails', request)
        .then(() => {
          // Reset news
          store.state.hasNews = true;
        });
    },

    async sendContactEmail(_, payload: IContactRequest) {
      return contactFormService.sendContactFormData(payload);
    },

    async getGeoCoordinatesByAddress({ commit }) {
      const internalOrderId = store.state.order.internalOrderId;
      const response = await trackingService.GetCoordinates(internalOrderId);
      commit('SET_CLIENTLNGLAT', response);

      return null;
    },

    initStickyBottom({ commit }) {
      commit('SET_STICKYBOTTOM');
    },

    updateStickyBottom({ commit }) {
      const stickyBottomIsMin = store.state.stickyBottom == store.state.minStickyBottom;

      if (window.innerWidth <= 800) {
        // make sure that the sticky banner is reset to the correct bottom, if previously changed in desktop view
        if (!stickyBottomIsMin)
          commit('SET_STICKYBOTTOM');
      } else {
        const visibleFooterHeight = viewportHelper.getVisibleFooterHeight();

        if (visibleFooterHeight) {
          const newBottom = visibleFooterHeight + 10;
          if (newBottom > store.state.minStickyBottom)
            commit('SET_STICKYBOTTOM', newBottom);

        } else if (!stickyBottomIsMin) {
          commit('SET_STICKYBOTTOM');
        }
      }
    },

    setNewRequest({ commit }) {
      commit('NEW_REQUEST');
    },

    async getDeliveryTruckMapData({ commit }) {
        try {
          const response = await trackingService.getDeliveryTruckMapData(
            store.state.order.internalOrderId
          );
          commit("UPDATE_DELIVERY_TRUCK_MAP_DATA", response.data);
        } catch (error) {
          console.error("Failed to get delivery truck map data:", error);
        }
    }
  },
  plugins: [vuexLocalStorage.plugin]
});

export default store;
